import * as React from "react";
import { useState, useMemo } from "react";
import styled from "styled-components";
import { Formik, Form, Field } from "formik";
import { describeExpression } from "util/CronUtils";
import { Button, ControlLabel, Input } from "components/bootstrap";
import { Switch, Spinner } from "components/common";
import { MarkdownPreview } from "components/common/MarkdownEditor";
import { Row, Col } from "security-app/components/common/FormBaseStyles";
import { DataWell } from "common/components/layout-styles";
import {
  SubmitOnValueChange,
  FormikMultiSelect,
  FormikTimeUnitInput,
  FormikUserTimezoneSelect,
  FormikSearchFiltersFormControl,
  useStreamOptions,
  useNotificationOptions
} from "security-app/components/common/FormikComponents";
import {
  EventProcedureFormField,
  ShareFormFormikField
} from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureForm";
import EventProcedureSummary from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureSummary";
import RuleOptionsReadOnly from "./ReadOnly";
import { SEARCH_WITHIN_UNITS, EXECUTE_EVERY_UNITS, validate } from "./utils";
const StyledForm = styled(Form)`
  width: 100%;

  & .form-group {
    width: 100%;
  }
`;
const StyledHelp = styled.small`
  white-space: pre-wrap;
`;
function RuleOptionsForm({
  initialValues,
  formId = "ruleOptionsForm",
  onSubmit,
  onCancel = () => {
  },
  validateOnMount = false,
  isEditing = false,
  autoSubmit = false,
  formState = () => {
  },
  notMutable = false,
  readOnly = false,
  isTemporal = false
}) {
  const { streamOptions, streamCategoryOptions, loadingStreams } = useStreamOptions();
  const { notificationOptions, loadingNotifications } = useNotificationOptions();
  const [showAddEventProcedureForm, setShowAddEventProcedureForm] = useState(false);
  const eventProcedureId = initialValues == null ? void 0 : initialValues.event_procedure_id;
  const showEventProcedureSummary = useMemo(
    () => !!eventProcedureId && !showAddEventProcedureForm,
    [eventProcedureId, showAddEventProcedureForm]
  );
  const showAddNewEventProcedure = useMemo(
    () => !eventProcedureId && !showAddEventProcedureForm && !readOnly && !notMutable,
    [eventProcedureId, showAddEventProcedureForm, readOnly, notMutable]
  );
  const showRemediationSteps = useMemo(
    () => !!initialValues.remediation_steps && !initialValues.event_procedure_id && !showAddEventProcedureForm,
    [initialValues.remediation_steps, initialValues.event_procedure_id, showAddEventProcedureForm]
  );
  const handleCancel = (resetForm) => () => {
    resetForm();
    onCancel();
  };
  const preventSubmitOnEnter = (e) => e.key !== "Enter";
  if (readOnly) return /* @__PURE__ */ React.createElement(RuleOptionsReadOnly, { ruleOptions: initialValues });
  return /* @__PURE__ */ React.createElement(Formik, { onSubmit, initialValues, validate, validateOnMount }, ({ values, errors, isValid, dirty, submitForm, resetForm, setFieldValue }) => /* @__PURE__ */ React.createElement(StyledForm, { id: formId, onKeyDown: preventSubmitOnEnter }, /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(Field, { name: "event_summary_template" }, ({ field }) => {
    var _a;
    return /* @__PURE__ */ React.createElement(
      Input,
      {
        id: field.name,
        name: field.name,
        label: "Event Summary Template",
        type: "text",
        value: (_a = field.value) != null ? _a : "",
        onChange: ({ target }) => setFieldValue(field.name, target.value === "" ? null : target.value),
        onBlur: field.onBlur,
        disabled: notMutable,
        help: /* @__PURE__ */ React.createElement(StyledHelp, null, "Template used to build the Event/Alert summary for this rule.")
      }
    );
  }), loadingStreams ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading stream options" }) : !isTemporal && /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "streams",
      component: FormikMultiSelect,
      options: streamOptions,
      value: values.streams,
      label: "Streams",
      multiple: true,
      menuPlacement: "bottom",
      disabled: notMutable || isTemporal
    }
  ), streamCategoryOptions.length > 0 && !isTemporal && /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "stream_categories",
      component: FormikMultiSelect,
      options: streamCategoryOptions,
      value: values.stream_categories,
      label: "Stream categories",
      multiple: true,
      menuPlacement: "bottom",
      disabled: notMutable || isTemporal
    }
  ), loadingNotifications ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading notifications" }) : /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "notifications",
      component: FormikMultiSelect,
      options: notificationOptions,
      value: values.notifications,
      label: "Notifications",
      multiple: true,
      menuPlacement: "bottom"
    }
  ), /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "search_within",
      component: FormikTimeUnitInput,
      label: "Search within",
      value: values.search_within,
      units: SEARCH_WITHIN_UNITS,
      errors,
      disabled: notMutable
    }
  ), /* @__PURE__ */ React.createElement(Row, { $width: "100%", $fullWidth: true }, /* @__PURE__ */ React.createElement(Field, { name: "use_cron_scheduling", type: "checkbox" }, ({ field }) => /* @__PURE__ */ React.createElement(
    Switch,
    {
      name: field.name,
      id: `${formId}_${field.name}`,
      label: "Use cron scheduling",
      checked: field.checked,
      onChange: field.onChange,
      disabled: notMutable
    }
  ))), values.use_cron_scheduling ? /* @__PURE__ */ React.createElement(Col, { $width: "100%", $fullWidth: true }, /* @__PURE__ */ React.createElement(Field, { name: "cron_expression", style: { width: "100%" } }, ({ field }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: field.name,
      name: field.name,
      label: "Cron expression",
      type: "text",
      value: field.value,
      onChange: field.onChange,
      onBlur: field.onBlur,
      help: /* @__PURE__ */ React.createElement(StyledHelp, null, describeExpression(field.value) || "A Quartz cron expression to determine when the event should be run."),
      bsStyle: field.error && field.touched ? "error" : null
    }
  )), /* @__PURE__ */ React.createElement(Field, { component: FormikUserTimezoneSelect, id: "cron_timezone", name: "cron_timezone" })) : /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "execute_every",
      component: FormikTimeUnitInput,
      label: "Execute every",
      value: values.execute_every,
      units: EXECUTE_EVERY_UNITS,
      errors,
      disabled: notMutable
    }
  ), !isTemporal && /* @__PURE__ */ React.createElement(Row, { $fullWidth: true, $width: "100%" }, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikSearchFiltersFormControl,
      name: "filters",
      value: values.filters,
      label: "Search Filters"
    }
  )), showAddEventProcedureForm && /* @__PURE__ */ React.createElement(Row, { $fullWidth: true, $width: "100%" }, /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "event_procedure_id",
      id: "event_procedure_id",
      value: initialValues == null ? void 0 : initialValues.event_procedure_id,
      component: EventProcedureFormField,
      remediationSteps: values.remediation_steps,
      onClose: () => setShowAddEventProcedureForm(false),
      onRemove: () => setShowAddEventProcedureForm(false)
    }
  )), showRemediationSteps && /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Remediation Steps"), /* @__PURE__ */ React.createElement(DataWell, null, /* @__PURE__ */ React.createElement(
    MarkdownPreview,
    {
      value: initialValues.remediation_steps,
      maxHeight: 500,
      show: true,
      noBackground: true,
      noBorder: true,
      noPadding: true
    }
  ))), showEventProcedureSummary && /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Event Procedure Summary"), /* @__PURE__ */ React.createElement(
    EventProcedureSummary,
    {
      eventProcedureId: initialValues.event_procedure_id,
      canEdit: !notMutable,
      onEdit: () => setShowAddEventProcedureForm(true),
      onRemove: () => {
        setFieldValue("event_procedure_id", "");
        setShowAddEventProcedureForm(false);
      }
    }
  )), showAddNewEventProcedure && /* @__PURE__ */ React.createElement(Col, { $gap: "0.4rem" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Event Procedures"), /* @__PURE__ */ React.createElement("p", null, "This rule does not have any Event Procedures yet."), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => setShowAddEventProcedureForm(true) }, "Add Event Procedure")), !isEditing && /* @__PURE__ */ React.createElement(
    Field,
    {
      name: "share_request",
      id: "share_request",
      value: values.share_request,
      component: ShareFormFormikField,
      description: "Search for a User or Team to add as collaborator on the uploaded sigma rules.",
      entityType: "sigma_rule"
    }
  ), autoSubmit ? /* @__PURE__ */ React.createElement(SubmitOnValueChange, { formState }) : /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Button, { type: "reset", bsSize: "xs", onClick: handleCancel(resetForm) }, "Cancel"), /* @__PURE__ */ React.createElement(Button, { type: "button", bsStyle: "primary", bsSize: "xs", onClick: submitForm, disabled: !isValid || !dirty }, "Save Options")))));
}
export default RuleOptionsForm;
